import React from 'react';
import { baseURL } from '../../api/baseURL';
import axios from 'axios';
import { connect } from 'react-redux';
import Loader from '../../Loader';
import Modal from '../../Modal';
import AdminShell from '../layout/AdminShell';
import { countPillStyle, chipBarStyle, chipStyle, thStyle } from '../layout/listUi';

type RatingRow = {
    receipt_id: string;
    customer_id: string;
    customer_name: string;
    category: string;
    photographer_id: string;
    photographer_name: string;
    service_type: string;
    stars: number;
    comment: string;
    rated_at: string;
    event_from: string;
};

type State = {
    requested: boolean,
    err: boolean,
    ratings: RatingRow[],
    total: number,
    maxStarsFilter: string,
    // Client-reported: add separate 4-star and 5-star options — the
    // existing chips are all "N stars or below" (worst-first triage), which
    // can't isolate a single exact star count on its own.
    exactStarsFilter: string,
};

/** Photographer -> customer ratings — internal only, never shown to
 * customers or other photographers. Lets admin spot problem customers
 * (worst-rated first by default). */
class CustomerRatingsPage extends React.Component<{ history: any, token: string }> {

    state: State = {
        requested: false,
        err: false,
        ratings: [],
        total: 0,
        maxStarsFilter: '',
        exactStarsFilter: '',
    }

    componentDidMount(): void {
        if (!this.props.token) {
            window.location.pathname = "/";
        }
        else {
            this.getList();
        }
    }

    getList = () => {
        if (!this.state.requested) {
            this.setState({ err: false, requested: true });
            const maxStarsQuery = this.state.maxStarsFilter ? `&max_stars=${this.state.maxStarsFilter}` : '';
            const exactStarsQuery = this.state.exactStarsFilter ? `&exact_stars=${this.state.exactStarsFilter}` : '';
            axios.get(`${baseURL}/customer-ratings?limit=200${maxStarsQuery}${exactStarsQuery}`,
                {
                    headers: { Authorization: `Bearer ${this.props.token}` }
                })
                .then((response: any) => {
                    if (response?.data?.message === "success") {
                        this.setState({ ratings: response?.data?.ratings || [], total: response?.data?.total || 0, requested: false, err: false })
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch(() => {
                    this.setState({ err: true, requested: false });
                })
        }
    }

    toggleModal = () => {
        if (this.state.err) {
            this.setState({ err: false });
        }
    }

    onFilterChange = (value: string) => {
        this.setState({ maxStarsFilter: value, exactStarsFilter: '' }, () => this.getList());
    }

    onExactFilterChange = (value: string) => {
        this.setState({ exactStarsFilter: value, maxStarsFilter: '' }, () => this.getList());
    }

    render() {
        const STAR_FILTER_CHIPS = [
            { key: '', label: 'All ratings' },
            { key: '1', label: '1 star only' },
            { key: '2', label: '2 stars or below' },
            { key: '3', label: '3 stars or below' },
        ];
        const EXACT_STAR_FILTER_CHIPS = [
            { key: '4', label: '4 stars' },
            { key: '5', label: '5 stars' },
        ];
        return (
            <AdminShell activePage="customer-ratings" title="Customer Ratings" subtitle="Photographer → customer ratings (internal only)">
                <Modal open={this.state.err} hideSec primaryText="Okay" title='Update' onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>Something went wrong, please try again later</h6>} />
                {this.state.requested ? <Loader /> : null}
                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title">
                            Customer Ratings (from Snapmasters)
                            {this.state.total ? <span style={countPillStyle}>{this.state.total}</span> : null}
                        </h4>
                        <p style={{ color: '#888', marginTop: -4, marginBottom: 12 }}>
                            Internal only — these ratings are never shown to customers or other photographers.
                        </p>
                        <div style={chipBarStyle}>
                            {STAR_FILTER_CHIPS.map(chip => (
                                <div
                                    key={chip.key}
                                    style={chipStyle(this.state.maxStarsFilter === chip.key && (chip.key !== '' || !this.state.exactStarsFilter))}
                                    onClick={() => this.onFilterChange(chip.key)}
                                >
                                    {chip.label}
                                </div>
                            ))}
                            {EXACT_STAR_FILTER_CHIPS.map(chip => (
                                <div
                                    key={chip.key}
                                    style={chipStyle(this.state.exactStarsFilter === chip.key)}
                                    onClick={() => this.onExactFilterChange(chip.key)}
                                >
                                    {chip.label}
                                </div>
                            ))}
                        </div>
                        <div className="table-responsive">
                            {(this.state.ratings?.length) ? <table className="table">
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Customer</th>
                                        <th style={thStyle}>Stars</th>
                                        <th style={thStyle}>Comment</th>
                                        <th style={thStyle}>Snapmaster</th>
                                        <th style={thStyle}>Service</th>
                                        <th style={thStyle}>Order</th>
                                        <th style={thStyle}>Rated On</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.ratings.map((item: RatingRow) => (
                                        <tr key={`${item.receipt_id}-${item.photographer_id}`}>
                                            <td>{item.customer_name || item.customer_id || '—'}</td>
                                            <td>
                                                <span className={`badge ${item.stars <= 2 ? 'badge-outline-danger' : item.stars === 3 ? 'badge-outline-warning' : 'badge-outline-success'}`}>
                                                    {'★'.repeat(item.stars)}{'☆'.repeat(5 - item.stars)}
                                                </span>
                                            </td>
                                            <td>{item.comment || '—'}</td>
                                            <td>{item.photographer_name || '—'}</td>
                                            <td>{item.service_type || item.category || '—'}</td>
                                            <td>{item.receipt_id}</td>
                                            <td>{item.rated_at ? new Date(item.rated_at).toLocaleString() : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table> : <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                                <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No Data</div>
                            </div>}
                        </div>
                    </div>
                </div>
            </AdminShell>
        );
    }
}

const mapDispatchToProps = (dispatch: Function) => {
    return {
        dispatch
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        token: state.auth.token
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerRatingsPage);
